import styled, { css } from 'styled-components'

import { LinkToOutlet } from '@src/components/LinkToOutlet'
import { breakpoints } from '@src/constants/breakpoints'

import { SingleOutletDirection } from './types'

import { Button } from '../Button'
import { DotSpan } from '../DotSpan/DotSpan'
import { CouponManySVG } from '../SVGS/CouponManySVG'
import { CouponSVG } from '../SVGS/CouponSVG'

export const LongSquareButton = styled(Button)<{ status: string }>`
  height: 28px;
  width: auto;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 0 14px;
  min-height: 28px;
  border: none;

  @media (min-width: ${breakpoints.tablet}px) {
    height: 28px;
  }
  text-transform: uppercase;

  ${({ theme, status }) => {
    if (status === 'open' || status === 'order') {
      return css`
        color: ${theme.colors.openStatus};
        background-color: ${`${theme.colors.openStatus}20`};
      `
    }
    if (status === 'pre_order') {
      return css`
        color: ${theme.colors.preorderStatus};
        background-color: ${`${theme.colors.preorderStatus}20`};
      `
    }
    return css`
      color: #939393;
      background-color: #93939320;
    `
  }}
`

export const CoverImage = styled.div<{
  imageUrl: string
}>`
  height: 164px;
  background-color: #c4c4c4;
  border-radius: 8px 8px 0 0;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: transform 0.3s ease-in-out;
    ${props =>
      props.imageUrl &&
      css`
        background-image: url(${props.imageUrl});
      `};
  }

  @media (min-width: ${breakpoints.tablet}px) {
    height: 176px;
  }
`

export const RestaurantContainer = styled.div<{
  direction: SingleOutletDirection
  hasOffer: boolean
  isSlide: boolean
}>`
  all: unset;
  display: flex;
  flex-direction: ${props => props.direction};
  border-radius: 8px;
  position: relative;
  transition: all 0.3s;
  cursor: pointer;
  margin: 0 0 8px 0;
  margin-top: ${({ isSlide }) => (isSlide ? '0px' : '8px')};
  background-color: ${({ theme }) => theme.colors.brandForeground};
  height: 288px;
  overflow: hidden;

  @media (min-width: ${breakpoints.tablet}px) {
    margin-top: ${({ isSlide }) => (isSlide ? '0px' : '24px')};
    height: 300px;
  }

  ${props =>
    props.direction === SingleOutletDirection.ROW &&
    css`
      @media (max-width: ${breakpoints.tablet}px) {
        ${CoverImage} {
          height: 100%;
          max-height: 249px;
          border-radius: 8px 8px 8px 8px;
          flex-shrink: 0;

          @media (min-width: ${breakpoints.tablet}px) {
            width: 32%;
            border-radius: 4px 0 0 4px;
          }
        }

        ${OutletName} {
          margin-top: 0px;
        }

        ${OutletDetailsContainer} {
          width: 59%;
          height: 100%;

          @media (min-width: ${breakpoints.phablet}px) {
            width: 68%;
          }
        }

        ${OutletOpeningContainer} {
          @media (min-width: ${breakpoints.phablet}px) {
            flex-direction: row;
          }
        }

        ${OpenStatusContainer} {
          @media (max-width: ${breakpoints.phablet}px) {
          }
        }
      }
    `}
`

export const LinkToOutletContainer = styled(LinkToOutlet)<{ status: string }>`
  box-sizing: border-box;
  margin: 0 0 0 12px;
  width: 260px;

  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.tablet}px) {
    width: 298px;
    margin: 0 0 0 28px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${RestaurantContainer} {
        box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;

        h5 {
          color: ${({ theme }) => theme.colors.brand};
        }

        ${CoverImage} {
          &::before {
            transform: scale(1.05); /* Adjust the scale as needed */
          }
        }
      }

      ${LongSquareButton} {
        ${({ theme, status }) => {
          if (status === 'open' || status === 'order') {
            return css`
              color: #fff;
              background-color: ${theme.colors.openStatus};
            `
          }
          if (status === 'pre_order') {
            return css`
              color: #fff;
              background-color: ${theme.colors.preorderStatus};
            `
          }
          return css`
            color: #fff;
            background-color: #939393;
          `
        }}
      }
    }
  }
`

export const InfoContainer = styled.div`
  @media (min-width: ${breakpoints.tablet}px) {
    flex: 1;
  }
`

export const CuisineDot = styled(DotSpan)`
  span {
    margin-right: 6px;
  }
`

export const OutletDetailsContainer = styled.div<{
  isSlide: boolean
}>`
  padding: 0px 12px 16px 14px;
  margin: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;

  @media (min-width: ${breakpoints.tablet}px) {
    width: auto;
  }
`

export const OutletName = styled.h5`
  margin: 0px;
  font-size: 14px;
  font-weight: 700;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.mainText};

  @media (min-width: ${breakpoints.tablet}px) {
    font-size: 16px;
  }
`

export const NameAndRatingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

export const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.mainText};
`

export const CuisinesContainer = styled.div<{ isSlide: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0;

  align-content: flex-start;
  flex: 1;
  height: 24px;

  @media (min-width: ${breakpoints.tablet}px) {
    margin-bottom: 0;
  }
`

export const CuisineTag = styled.div`
  padding: -10px;
  display: flex;
  flex-direction: row;
  font-size: 10px;

  @media (min-width: ${breakpoints.tablet}px) {
    font-size: 12px;
  }

  span {
    margin-right: 4px;
    display: flex;
    justify-items: center;
    align-items: center;
    padding-bottom: 2px;

    @media (min-width: ${breakpoints.tablet}px) {
      margin-right: 5px;
      height: 14px;
    }
  }
`

export const Cuisine = styled.p`
  margin: 0 6px 0 0;
  font-size: 12px;
  font-size: 12px;
  line-height: 16px;
  opacity: 0.6;

  @media (min-width: ${breakpoints.tablet}px) {
    margin-right: 4px;
  }
`

export const OutletOpeningContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`

export const OutletTranslationContainer = styled.div`
  font-size: 12px;

  flex-direction: row;
  flex-wrap: wrap;
  display: flex;
  margin: 0 4px 0 0;
`

export const OutletTranslationText = styled.div<{
  openingStatus: string
  isOutletPage: boolean | undefined
}>`
  font-weight: 700;
  color: ${({ openingStatus, theme }) => {
    if (openingStatus === 'open' || openingStatus === 'order') {
      return theme.colors.openStatus
    }
    if (openingStatus === 'pre_order') {
      return theme.colors.preorderStatus
    } else return 'black'
  }};
`

export const OpenStatusContainer = styled.div`
  height: 32px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: auto;
  justify-content: flex-end;
`

export const PaddedDotSpan = styled.span`
  margin: 0 2px 0 4px;
`

export const RestaurantLogo = styled.div<{
  imageUrl: string
  isSlide: boolean
}>`
  ${({ imageUrl, isSlide }) => css`
    height: 80px;
    width: 80px;
    top: 50px;
    left: 16px;
    border-radius: 4px;
    background-size: cover;
    background-position: 50%;
    margin-right: 12px;
    z-index: 1;

    ${imageUrl &&
    css`
      background-image: url(${imageUrl});
    `};

    ${isSlide &&
    css`
      height: 72px;
      width: 72px;
      position: absolute;
      margin-right: 0;
    `}

    @media (min-width: ${breakpoints.tablet}px) {
      height: 72px;
      top: 56px;
      width: 72px;
      margin-right: 0;
    }
  `}
`

export const DiscountName = styled.p`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  margin: 0;
  padding: 0px 24px 0 16px;
  overflow: hidden;
`

export const StyledDiscountSVG = styled(CouponSVG)`
  width: 16px;
  height: 16px;
  margin-right: 6px;
`

export const StyledDiscountManySVG = styled(CouponManySVG)`
  width: 16px;
  height: 16px;
  margin-right: 6px;
`

export const DiscountContainer = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  color: ${({ theme }) => theme.colors.brandForeground};
  width: fit-content;
  border-radius: 0px 16px 16px 0px;
  background-color: ${({ theme }) => theme.colors.discount};
  position: relative;
  top: 124px;
  z-index: 2;
  margin-left: 0px;
  border-radius: 0px;
  width: 100%;
  opacity: 0.9;
  @media (min-width: ${breakpoints.tablet}px) {
    top: 144px;
  }
`

export const DiscountsContainer = styled.div`
  margin: 8px 0px;
  position: absolute;
  width: 260px;

  @media (min-width: ${breakpoints.tablet}px) {
    width: 298px;
    margin: 0px;
    height: 0;
    display: flex;
  }
`

export const OfferContainer = styled.div`
  text-align: left;
  background: ${({ theme }) => theme.colors.flag};
  color: ${({ theme }) => theme.colors.brandForeground};
  border-radius: 0px 8px 8px 0px;
  display: flex;
  align-items: flex-start;
  padding: 2px 10px 2px 16px;
  position: absolute;
  z-index: 1;
  top: 12px;
  left: 12px;
  margin-right: 8px;

  @media (min-width: ${breakpoints.tablet}px) {
    max-width: 270px;
    padding: 0px 10px 0px 16px;
    border-radius: 0px 8px 8px 0px;
    position: absolute;
    z-index: 1;
    top: 14px;
    left: 28px;
    margin-right: 8px;
  }
`

export const OfferText = styled.p`
  width: 100%;
  font-size: 12px;
  font-weight: 700;
  margin: 6px 6px 6px 0;
  @media (min-width: ${breakpoints.tablet}px) {
    margin: 8px 0px;
    line-height: 14px;
  }
`
export const DiscountsAndLoyaltyCardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`

export const OrderInfoContainer = styled.div`
  width: 100%;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.mainText};
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  span {
    margin-right: 8px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
`

export const LoyaltyCardStampAndIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0px 0 6px;
  height: 16px;
`

export const LoyaltyCardTextContainer = styled.div<{
  textColor: string
  numberOfCards: number
}>`
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: ${({ textColor, numberOfCards, theme }) => {
    if (numberOfCards > 1) {
      return theme.colors.discount
    } else return textColor
  }};
`

export const ImageContainer = styled.div<{ imageCount: number }>`
  display: flex;

  flex-direction: row;
  align-items: center;
  margin-right: ${({ imageCount }) =>
    `calc(24px + ( ${imageCount - 1} * 10px ))`};

  position: relative;
`

export const LoyaltyCardImage = styled.div<{
  imageUrl: string
  index: number
  imageCount: number
  scale: number
}>`
  background-size: cover;
  min-width: 19px;
  min-height: 19px;
  background-color: grey;
  filter: drop-shadow(-2px 2px 2px rgba(0, 0, 0, 0.2));
  position: absolute;
  left: ${({ index, imageCount }) => (imageCount - index - 1) * 10}px;

  ${props =>
    props.imageUrl &&
    css`
      background-image: url(${props.imageUrl});
    `};
  clip-path: url(#scalloped-mask-${({ scale }) => scale});
`
